import React from 'react'
import { 
  ClockIcon, 
  MapPinIcon, 
  ExclamationTriangleIcon,
  UserGroupIcon,
  CheckCircleIcon,
  PlusIcon,
  CalendarDaysIcon,
  UserIcon,
  TrashIcon,
  HandRaisedIcon,
  XMarkIcon as ReleaseIcon,
  PencilIcon,
  LinkIcon
} from '@heroicons/react/24/outline'
import { formatDateLocal, formatTime } from '../utils/dateUtils'

interface ScheduledEvent {
  id: string
  time: string
  title: string
  description: string
  location?: string
  eventUrl?: string
  coordinator?: string
  coordinatorName?: string
  isOptional?: boolean
  maxParticipants?: number
  deadline?: string
  rsvps?: string[]
}

interface FoodItem {
  id: string
  category: string
  description: string
  assignedTo?: string
  assignedUserName?: string
  servedDate?: string
}

interface DailyScheduleProps {
  date: string
  events: ScheduledEvent[]
  foodItems: FoodItem[]
  currentUserId?: string
  canEdit?: boolean
  isAdmin?: boolean
  isExpired?: boolean
  onRSVP?: (eventId: string, attending: boolean) => void
  onAddEvent?: (date: string) => void
  onAddFood?: (date: string) => void
  onClaimFood?: (slotId: string) => void
  onReleaseFood?: (slotId: string) => void
  onEditFood?: (foodItem: FoodItem) => void
  onDeleteFood?: (foodId: string) => void
  onEditEvent?: (date: string, scheduledEventId: string) => void
  onDeleteEvent?: (date: string, scheduledEventId: string) => void
  onRsvpEvent?: (date: string, scheduledEventId: string, attending: boolean) => void
  onEventClick?: (date: string, scheduledEventId: string) => void
}

const DailySchedule: React.FC<DailyScheduleProps> = ({ 
  date, 
  events, 
  foodItems,
  currentUserId, 
  canEdit = false,
  isAdmin = false,
  isExpired = false,
  onRSVP,
  onAddEvent,
  onAddFood,
  onClaimFood,
  onReleaseFood,
  onEditFood,
  onDeleteFood,
  onEditEvent,
  onDeleteEvent,
  onRsvpEvent,
  onEventClick
}) => {
  const isDeadline = (event: ScheduledEvent) => {
    return event.deadline !== undefined
  }

  const isUserRSVPed = (event: ScheduledEvent) => {
    if (!event.rsvps || !currentUserId) return false
    return event.rsvps.some(rsvp => 
      typeof rsvp === 'string' ? rsvp === currentUserId : rsvp.userId === currentUserId
    )
  }

  const getRSVPCount = (event: ScheduledEvent) => {
    return event.rsvps?.length || 0
  }

  const hasEvents = events.length > 0
  const hasFoodItems = foodItems.length > 0
  const hasAnyItems = hasEvents || hasFoodItems

  return (
    <div className="space-y-3">
      {/* Day Header with Action Buttons */}
      <div className="flex justify-between items-center border-b border-gray-200 dark:border-gray-700 pb-3">
        <h4 className="text-md font-medium text-gray-900 dark:text-white">
          {formatDateLocal(date, { 
            weekday: 'long', 
            month: 'long', 
            day: 'numeric' 
          })}
        </h4>
        {canEdit && (
          <div className="flex space-x-2">
            <button
              onClick={() => onAddEvent?.(date)}
              className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 border border-primary-200 dark:border-primary-700 transition-colors"
            >
              <PlusIcon className="w-4 h-4 mr-1" />
              Add Event
            </button>
            <button
              onClick={() => onAddFood?.(date)}
              className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 border border-primary-200 dark:border-primary-700 transition-colors"
            >
              <PlusIcon className="w-4 h-4 mr-1" />
              Add Food
            </button>
          </div>
        )}
      </div>

      {hasAnyItems ? (
        <div className="space-y-4">
          {/* Events Section */}
          {hasEvents && (
            <div className="space-y-3">
              <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                Activities & Events
              </h5>
              <div className="space-y-3">
                {events
                  .sort((a, b) => a.time.localeCompare(b.time))
                  .map((event) => (
                  <div
                    key={event.id}
                    className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-blue-50 dark:bg-blue-900/20 cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                    onClick={() => onEventClick?.(date, event.id)}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="text-sm font-medium text-primary-600 dark:text-primary-400">
                            {formatTime(event.time)}
                          </span>
                          {event.isOptional && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                              Optional
                            </span>
                          )}
                          {event.deadline && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                              Deadline
                            </span>
                          )}
                        </div>
                        
                        <h5 className="font-medium text-gray-900 dark:text-white mb-1">
                          {event.title}
                        </h5>
                        
                        {event.description && (
                          <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                            {event.description}
                          </p>
                        )}

                        <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                          {event.location && (
                            <div className="flex items-center space-x-1">
                              <MapPinIcon className="w-4 h-4" />
                              <span>{event.location}</span>
                            </div>
                          )}
                          {event.coordinatorName && (
                            <div className="flex items-center space-x-1">
                              <UserIcon className="w-4 h-4" />
                              <span>by {event.coordinatorName}</span>
                            </div>
                          )}
                          {event.eventUrl && (
                            <div className="flex items-center space-x-1">
                              <LinkIcon className="w-4 h-4" />
                              <a
                                href={event.eventUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 underline"
                                onClick={(e) => e.stopPropagation()}
                              >
                                Visit Website
                              </a>
                            </div>
                          )}
                          {event.maxParticipants && (
                            <div className="flex items-center space-x-1">
                              <UserGroupIcon className="w-4 h-4" />
                              <span>Max {event.maxParticipants}</span>
                            </div>
                          )}
                        </div>

                        {event.deadline && (
                          <div className="mt-2 text-xs text-red-600 dark:text-red-400 flex items-center space-x-1">
                            <ClockIcon className="w-3 h-3" />
                            <span>
                              Deadline: {new Date(event.deadline).toLocaleString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true
                              })}
                            </span>
                          </div>
                        )}

                        {/* RSVP Section */}
                        <div className="mt-3 flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="flex items-center space-x-1 text-sm text-gray-600 dark:text-gray-400">
                              <UserGroupIcon className="w-4 h-4" />
                              <span>{getRSVPCount(event)} attending</span>
                            </div>
                            
                            {!isExpired && onRsvpEvent && (
                              <div className="flex items-center space-x-2">
                                {isUserRSVPed(event) ? (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      onRsvpEvent(date, event.id, false)
                                    }}
                                    className="inline-flex items-center px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900 dark:text-green-200 dark:hover:bg-green-800"
                                  >
                                    <CheckCircleIcon className="w-3 h-3 mr-1" />
                                    Attending
                                  </button>
                                ) : (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      onRsvpEvent(date, event.id, true)
                                    }}
                                    className="inline-flex items-center px-2 py-1 text-xs font-medium rounded bg-gray-100 text-gray-800 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                                  >
                                    <HandRaisedIcon className="w-3 h-3 mr-1" />
                                    RSVP
                                  </button>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Action Buttons */}
                          {!isExpired && (canEdit || isAdmin) && (
                            <div className="flex items-center space-x-2">
                              {(canEdit || isAdmin) && onEditEvent && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    onEditEvent(date, event.id)
                                  }}
                                  className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                                  title="Edit activity"
                                >
                                  <PencilIcon className="w-4 h-4" />
                                </button>
                              )}
                              
                              {isAdmin && onDeleteEvent && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    if (window.confirm('Are you sure you want to delete this activity? This action cannot be undone.')) {
                                      onDeleteEvent(date, event.id)
                                    }
                                  }}
                                  className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                                  title="Delete activity"
                                >
                                  <TrashIcon className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Food Items Section */}
          {hasFoodItems && (
            <div className="space-y-3">
              <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                Food & Beverages
              </h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {foodItems.map((item) => (
                  <div
                    key={item.id}
                    className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-green-50 dark:bg-green-900/20"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 dark:text-white">
                          {item.category}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-300">
                          {item.description}
                        </div>
                        {item.servedDate && (
                          <div className="text-xs text-blue-600 dark:text-blue-400 mt-1 flex items-center space-x-1">
                            <ClockIcon className="w-3 h-3" />
                            <span>
                              Served: {new Date(item.servedDate).toLocaleString('en-US', {
                                month: 'short',
                                day: 'numeric', 
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true
                              })}
                            </span>
                          </div>
                        )}
                        {item.assignedUserName && (
                          <div className="text-sm text-green-600 dark:text-green-400 mt-2 flex items-center space-x-1">
                            <CheckCircleIcon className="w-4 h-4" />
                            <span>Covered by {item.assignedUserName}</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="ml-4 flex flex-row space-x-2">
                        {!item.assignedTo && !isExpired && (
                          <button
                            onClick={() => onClaimFood?.(item.id)}
                            className="p-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                            title="I'll bring this"
                          >
                            <HandRaisedIcon className="w-4 h-4" />
                          </button>
                        )}
                        {item.assignedTo === currentUserId && (
                          <button
                            onClick={() => onReleaseFood?.(item.id)}
                            className="p-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                            title="Release"
                          >
                            <ReleaseIcon className="w-4 h-4" />
                          </button>
                        )}
                        {item.assignedTo === currentUserId && (
                          <button
                            onClick={() => onEditFood?.(item)}
                            className="p-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                            title="Edit"
                          >
                            <PencilIcon className="w-4 h-4" />
                          </button>
                        )}
                        {isAdmin && (
                          <button
                            onClick={() => onDeleteFood?.(item.id)}
                            className="p-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                            title="Delete"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-6 bg-gray-50 dark:bg-gray-700/30 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
          <CalendarDaysIcon className="mx-auto h-8 w-8 text-gray-400 dark:text-gray-500" />
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            No activities or food items scheduled for this day
          </p>
          {canEdit && (
            <div className="mt-3 space-x-2">
              <button
                onClick={() => onAddEvent?.(date)}
                className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
              >
                Add Event
              </button>
              <span className="text-gray-400">•</span>
              <button
                onClick={() => onAddFood?.(date)}
                className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
              >
                Add Food Item
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default DailySchedule
